import { ClimatePayload } from "../payloadTypes";
import { ClimateData, ClimateIndicator, FactRow } from "../types";
import {
  isFiniteInRange,
  toTrimmedString,
  hazardBandToLevel,
  warnOmitted,
  withHeroImageParams,
} from "./neighbourhoodHelpers";
import config from "@Config/index";

const KEY_PREFIX = "propertypage.components.neighbourhood.climate";

const heroImage = withHeroImageParams(
  `${config.IMAGE_STATIC_URL}/neighbourhood-assets/climate.png`,
);

// Climate badge severity translation keys: low → Mild, moderate → Fair,
// high → Severe. Resolved via Tolgee (with EN_DEFAULTS fallback) in ClimateTab.
const CLIMATE_LEVEL_KEY: Record<"low" | "moderate" | "high", string> = {
  low: `${KEY_PREFIX}.badge.value.mild`,
  moderate: `${KEY_PREFIX}.badge.value.fair`,
  high: `${KEY_PREFIX}.badge.value.severe`,
};

/**
 * Negative-signal guard (P1D-5482 FR-2). Only a Severe earthquake risk counts;
 * every other hazard band renders as normal.
 */
const isNegativeHazard = (hazardKey: string, level: "low" | "moderate" | "high"): boolean =>
  hazardKey === "earthquake" && level === "high";

/**
 * Transforms a ClimatePayload into the ClimateData view-model consumed by
 * ClimateTab. Reads nothing from `riskBadges`, `julyAverageTemperature`,
 * `airQuality.aqi`, or `disaster.source` (Requirements 5.20, 2.17 analogue).
 */
export const transformClimate = (
  climate: ClimatePayload | null | undefined,
  countryCode?: string,
): ClimateData => {
  const indicators: ClimateIndicator[] = [];
  const facts: FactRow[] = [];

  const isUS = (countryCode || "").trim().toUpperCase() === "US";

  // ─── Badges ─────────────────────────────────────────────────────────────────
  // Order: Air Quality, Flood, Earthquake, Snowfall, Wildfire — capped at 5.

  // Air Quality badge (Requirements 5.1, 5.6, 5.7, 5.8)
  const aqLabel = toTrimmedString(climate?.airQuality?.label);
  if (aqLabel !== undefined && aqLabel.length >= 1 && aqLabel.length <= 40) {
    const lower = aqLabel.toLowerCase();
    let level: "low" | "moderate" | "high";
    if (lower === "good") {
      level = "low";
    } else if (lower === "moderate") {
      level = "moderate";
    } else {
      // Catch-all: any other non-empty trimmed label 1–40 chars gets "high"
      level = "high";
    }
    indicators.push({
      labelKey: `${KEY_PREFIX}.badge.airQuality`,
      value: { kind: "key", key: CLIMATE_LEVEL_KEY[level] },
      level,
    });
  } else if (climate?.airQuality?.label != null) {
    warnOmitted("Air Quality badge", "climate.airQuality.label");
  }

  // Hazard badges: Earthquake, Snowfall.
  // Flood and Wildfire are deliberately absent — P1D-5482 FR-4 drops them from
  // the section entirely, whatever band the backend returns.
  const hazards = climate?.disaster?.hazards;
  const hazardEntries: Array<{
    field: string;
    key: string;
    raw: string | null | undefined;
  }> = [
    { field: "earthquake", key: "earthquake", raw: hazards?.earthquake },
    { field: "snowfall", key: "snowfall", raw: hazards?.snowfall },
  ];

  for (const entry of hazardEntries) {
    const trimmed = toTrimmedString(entry.raw);
    if (trimmed !== undefined) {
      const level = hazardBandToLevel(trimmed);
      if (level === undefined) {
        // Unrecognised band → omit badge (Requirement 5.21)
        warnOmitted(`${entry.key} badge`, `climate.disaster.hazards.${entry.field}`);
      } else if (isNegativeHazard(entry.key, level)) {
        // Negative signal (P1D-5482 FR-2): a Severe earthquake risk is hidden,
        // and the rest of the tab renders as normal.
        warnOmitted(
          `${entry.key} badge`,
          `climate.disaster.hazards.${entry.field} (negative signal)`,
        );
      } else {
        // Display severity as Mild / Fair / Severe based on the resolved level.
        indicators.push({
          labelKey: `${KEY_PREFIX}.badge.${entry.key}`,
          value: { kind: "key", key: CLIMATE_LEVEL_KEY[level] },
          level,
        });
      }
    }
  }

  // ─── Fact Rows ──────────────────────────────────────────────────────────────
  // Order: winter, summer difference, daylight
  // Temperatures: Fahrenheit for US, Celsius (raw backend value) otherwise.

  // Celsius to Fahrenheit helper
  const toF = (c: number): number => Math.round((c * 9) / 5 + 32);

  // Format an absolute temperature (backend value is Celsius) for display.
  const formatTemp = (c: number): string => (isUS ? `${toF(c)}°F` : `${Math.round(c)}°C`);
  // Format a temperature difference (backend value is a Celsius delta).
  const formatTempDiff = (c: number): string =>
    isUS ? `${Math.abs(Math.round((c * 9) / 5))}°F` : `${Math.abs(Math.round(c))}°C`;

  // Winter row: mild when Jan avg ≥ 45°F (≈ 7.2°C), cold below. The threshold is
  // a physical temperature, so it's evaluated the same way regardless of the
  // displayed unit.
  const janTemp = climate?.januaryAverageTemperature;
  if (isFiniteInRange(janTemp, -60, 60)) {
    const isMild = toF(janTemp) >= 45;
    facts.push({
      key: isMild ? `${KEY_PREFIX}.fact.winter.mild` : `${KEY_PREFIX}.fact.winter.cold`,
      params: { temp: formatTemp(janTemp) },
      trend: "down",
    });
  } else if (janTemp != null) {
    warnOmitted("winter Fact_Row", "climate.januaryAverageTemperature");
  }

  // Summer difference row
  const tempDiff = climate?.temperatureDifferenceFromNational;
  if (isFiniteInRange(tempDiff, -60, 60)) {
    // Whether the difference rounds to a non-zero value in the displayed unit.
    const roundedDiff = isUS
      ? Math.abs(Math.round((tempDiff * 9) / 5))
      : Math.abs(Math.round(tempDiff));
    if (tempDiff < 0 && roundedDiff > 0) {
      facts.push({
        key: `${KEY_PREFIX}.fact.summer.cooler`,
        params: { diff: formatTempDiff(tempDiff) },
        trend: "down",
      });
    } else if (tempDiff > 0 && roundedDiff > 0) {
      facts.push({
        key: `${KEY_PREFIX}.fact.summer.hotter`,
        params: { diff: formatTempDiff(tempDiff) },
        trend: "up",
      });
    } else {
      facts.push({
        key: `${KEY_PREFIX}.fact.summer.inLine`,
        trend: "neutral",
      });
    }
  } else if (tempDiff != null) {
    warnOmitted("summer difference Fact_Row", "climate.temperatureDifferenceFromNational");
  }

  // Daylight row
  const minutes = climate?.peakDaylight?.minutes;
  const formatted = toTrimmedString(climate?.peakDaylight?.formatted);
  if (isFiniteInRange(minutes, 0, 1440) && formatted !== undefined) {
    facts.push({
      key: `${KEY_PREFIX}.fact.daylight.peak`,
      params: { formatted },
      trend: "neutral",
    });
  } else if (minutes != null && formatted === undefined) {
    warnOmitted("daylight Fact_Row", "climate.peakDaylight.formatted");
  } else if (minutes != null) {
    warnOmitted("daylight Fact_Row", "climate.peakDaylight.minutes");
  }

  // ─── Card footer: seasonal range (P1D-5482) ─────────────────────────────────
  // Taken pre-formatted from the payload, in the unit the country reads in.
  const seasonalRange = toTrimmedString(
    isUS
      ? climate?.seasonalTemperatureRange?.formattedF
      : climate?.seasonalTemperatureRange?.formattedC,
  );

  // ─── Hero ───────────────────────────────────────────────────────────────────
  const annualTemp = climate?.annualAverageTemperature;
  const avgTemp = isFiniteInRange(annualTemp, -60, 60) ? formatTemp(annualTemp) : undefined;

  if (!isFiniteInRange(annualTemp, -60, 60) && annualTemp != null) {
    warnOmitted("hero avgTemp", "climate.annualAverageTemperature");
  }

  const result: ClimateData = {
    heroImage,
    tempLabelKey: `${KEY_PREFIX}.hero.label`,
    indicators,
    facts,
    sourceKey: `${KEY_PREFIX}.source`,
  };

  if (avgTemp !== undefined) {
    result.avgTemp = avgTemp;
  }

  if (seasonalRange !== undefined) {
    result.seasonalRange = seasonalRange;
  }

  return result;
};

export default transformClimate;
